// src/pages/BillingUpload.jsx
// The most important Role 1 screen — hospital billing staff uses this daily
// Design priority: foolproof. One action. Clear feedback.
// Supports drag-and-drop or click-to-browse
// Shows processing result with entry count, errors, and date gaps

import { useState, useCallback, useEffect, useRef } from "react";
import { api } from "../services/api";
import { AppSelect } from "../components/shared/FormControls";

const ACCEPTED = [".xlsx", ".xls", ".csv"];

export default function BillingUpload({ entity }) {
  const [dragging, setDragging]   = useState(false);
  const [file,     setFile]       = useState(null);
  const [profile,  setProfile]    = useState("");
  const [status,   setStatus]     = useState("idle"); // idle | uploading | done | error
  const [result,   setResult]     = useState(null);
  const [error,    setError]      = useState("");
  const inputRef = useRef();

  const mappingProfiles = entity?.columnMappingProfiles || [];

  useEffect(() => {
    if (mappingProfiles.length === 1 && !profile) {
      setProfile(mappingProfiles[0]._id);
    }
  }, [mappingProfiles, profile]);

  // ── File selection ──────────────────────────────────────────────────────
  function pickFile(f) {
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED.includes(ext)) {
      setError(`${f.name} is not allowed. Use .xlsx, .xls, or .csv`);
      return;
    }
    setFile(f);
    setError("");
    setResult(null);
    setStatus("idle");
    // Auto-select default profile
    const def = mappingProfiles.find(p => p.isDefault);
    if (def && !profile) setProfile(def._id);
  }

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  }, []);

  const onDragOver  = e => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()  => setDragging(false);

  // ── Upload ──────────────────────────────────────────────────────────────
  async function upload() {
    if (!file) return;
    if (mappingProfiles.length > 0 && !profile) {
      setError("Select a billing software profile before uploading.");
      return;
    }
    setStatus("uploading"); setError("");

    const fd = new FormData();
    fd.append("file", file);
    if (profile) fd.append("mappingProfileId", profile);

    try {
      const res = await api.upload(`/entities/${entity._id}/billing/upload`, fd);
      setResult(res);
      setStatus("done");
      setFile(null);
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }

  function reset() {
    setFile(null); setResult(null);
    setStatus("idle"); setError("");
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>

      {/* Page header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.h1}>Upload today's billing file</h1>
        <p style={styles.subtitle}>
          Export from your billing software and upload here. The system does the rest.
        </p>
      </div>

      {/* Entity badge */}
      <div style={styles.entityBadge}>
        <span style={styles.entityDot} />
        {entity?.name || "No entity selected"}
      </div>

      {mappingProfiles.length === 0 && (
        <div style={styles.warningBox}>
          <strong>No mapping profile found.</strong>
          <p style={{ margin: "4px 0 0" }}>
            Complete Column Mapping first. That setup file only teaches the system your columns; this Billing Upload screen creates journal entries.
          </p>
        </div>
      )}

      {/* Mapping profile selector */}
      {mappingProfiles.length > 0 && (
        <div style={styles.card}>
          <label style={styles.label}>Billing software</label>
          <AppSelect
            value={profile}
            onChange={setProfile}
            placeholder="Select"
            isClearable={mappingProfiles.length > 1}
            options={mappingProfiles.map(p => ({ value: p._id, label: p.profileName }))}
          />
          <p style={styles.hint}>Choose the profile that matches your billing software. If unsure, ask your administrator.</p>
        </div>
      )}

      {/* Drop zone */}
      {status !== "done" && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !file && inputRef.current.click()}
          style={{
            ...styles.dropZone,
            ...(dragging ? styles.dropZoneActive : {}),
            ...(file     ? styles.dropZoneFile   : {}),
            cursor: file ? "default" : "pointer",
          }}
        >
          <input
            ref={inputRef} type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={e => pickFile(e.target.files[0])}
          />

          {!file && (
            <>
              <div style={styles.dropIcon}>⬆</div>
              <div style={styles.dropTitle}>
                {dragging ? "Drop the file here" : "Drop your billing export here"}
              </div>
              <div style={styles.dropHint}>or click to browse — accepts .xlsx, .xls, .csv</div>
            </>
          )}

          {file && (
            <div style={{ textAlign: "center" }}>
              <div style={styles.fileIcon}>📄</div>
              <div style={styles.fileName}>{file.name}</div>
              <div style={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</div>
              <button
                onClick={e => { e.stopPropagation(); reset(); }}
                style={styles.removeBtn}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          <strong>Upload problem</strong><br />{error}
        </div>
      )}

      {/* Upload button */}
      {file && status !== "done" && (
        <button
          onClick={upload}
          disabled={status === "uploading"}
          style={{
            ...styles.btn,
            background: status === "uploading" ? "#90A4AE" : "#1B3F6E",
            cursor: status === "uploading" ? "not-allowed" : "pointer",
          }}
        >
          {status === "uploading" ? (
            <span>Processing… please wait</span>
          ) : (
            <span>Upload and process file</span>
          )}
        </button>
      )}

      {/* Uploading progress indicator */}
      {status === "uploading" && (
        <div style={styles.progressBar}>
          <div style={styles.progressFill} />
        </div>
      )}

      {/* Success result */}
      {status === "done" && result && (
        <div style={styles.resultCard}>
          <div style={styles.resultHeader}>
            <span style={styles.successIcon}>✓</span>
            <span style={styles.resultTitle}>File processed successfully</span>
          </div>

          <div style={styles.statsGrid}>
            <StatBox
              value={result.entriesGenerated}
              label="Journal entries created"
              color="#1B3F6E"
            />
            <StatBox
              value={result.parseErrors || 0}
              label="Rows with errors"
              color={result.parseErrors > 0 ? "#B71C1C" : "#1A7A4A"}
            />
            <StatBox
              value={result.flaggedRows || 0}
              label="Rows needing review"
              color={result.flaggedRows > 0 ? "#C85A00" : "#1A7A4A"}
            />
          </div>

          {result.dateGaps?.length > 0 && (
            <div style={styles.warningBox}>
              <strong>Missing dates detected</strong>
              <p style={{ margin: "4px 0 0" }}>
                No data found for: {result.dateGaps.join(", ")}. Upload those dates when available — cash tally for those dates is on hold.
              </p>
            </div>
          )}

          {result.parseErrors > 0 && (
            <div style={styles.errorBox}>
              <strong>{result.parseErrors} rows could not be processed.</strong><br />
              Your administrator has been notified. These rows need to be reviewed before the entry is finalised.
            </div>
          )}

          {result.flaggedRows > 0 && (
            <div style={styles.warnBox}>
              <strong>{result.flaggedRows} rows have an unrecognised payment mode.</strong><br />
              Your administrator will classify these. Everything else has been processed.
            </div>
          )}

          <button onClick={reset} style={{ ...styles.btn, marginTop: 20 }}>
            Upload another file
          </button>
        </div>
      )}

      {/* Help section */}
      <div style={styles.helpCard}>
        <div style={styles.helpTitle}>How to export from your billing software</div>
        <div style={styles.helpSteps}>
          <HelpStep n="1" text="Open your billing software (MOCDOC or whichever you use)" />
          <HelpStep n="2" text="Go to Reports → Daily Collection or similar section" />
          <HelpStep n="3" text="Select yesterday's date (or the date range you need)" />
          <HelpStep n="4" text="Export as Excel or CSV" />
          <HelpStep n="5" text="Upload that file here" />
        </div>
        <p style={{ fontSize: 12, color: "#90A4AE", margin: "12px 0 0" }}>
          Not sure how? Ask your administrator to show you once — it takes 30 seconds.
        </p>
      </div>

    </div>
  );
}

function StatBox({ value, label, color }) {
  return (
    <div style={{ textAlign: "center", padding: "16px 12px", background: "#f8fafc", borderRadius: 10 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: "#455A64", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function HelpStep({ n, text }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
      <span style={{
        background: "#1B3F6E", color: "#fff", borderRadius: "50%",
        width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
      }}>{n}</span>
      <span style={{ fontSize: 13, color: "#455A64" }}>{text}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  page:         { maxWidth: 640, margin: "0 auto" },
  pageHeader:   { marginBottom: 24 },
  h1:           { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 6px" },
  subtitle:     { fontSize: 14, color: "#607D8B", margin: 0 },
  entityBadge:  { display: "inline-flex", alignItems: "center", gap: 6, background: "#E3F2FD",
                  color: "#1B3F6E", fontSize: 12, fontWeight: 600, padding: "4px 12px",
                  borderRadius: 20, marginBottom: 20 },
  entityDot:    { width: 8, height: 8, borderRadius: "50%", background: "#2E86AB" },
  card:         { background: "#fff", borderRadius: 12, padding: "20px", marginBottom: 16,
                  boxShadow: "0 1px 4px rgba(0,0,0,.08)" },
  label:        { display: "block", fontSize: 12, fontWeight: 600, color: "#455A64",
                  textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 },
  select:       { width: "100%", padding: "10px 14px", border: "1.5px solid #CFD8DC",
                  borderRadius: 8, fontSize: 14, color: "#1a1a1a", background: "#fff",
                  fontFamily: "inherit", cursor: "pointer" },
  hint:         { fontSize: 12, color: "#90A4AE", margin: "8px 0 0" },
  dropZone:     { border: "2px dashed #CFD8DC", borderRadius: 14, padding: "48px 24px",
                  textAlign: "center", background: "#fafbfc", marginBottom: 16,
                  transition: "all .2s" },
  dropZoneActive:{ borderColor: "#2E86AB", background: "#E3F2FD" },
  dropZoneFile:  { borderColor: "#1B3F6E", background: "#EBF5FB", borderStyle: "solid" },
  dropIcon:     { fontSize: 36, marginBottom: 12, color: "#B0BEC5" },
  dropTitle:    { fontSize: 16, fontWeight: 600, color: "#37474F", marginBottom: 6 },
  dropHint:     { fontSize: 13, color: "#90A4AE" },
  fileIcon:     { fontSize: 40, marginBottom: 8 },
  fileName:     { fontSize: 15, fontWeight: 600, color: "#1B3F6E", marginBottom: 4 },
  fileSize:     { fontSize: 12, color: "#90A4AE", marginBottom: 12 },
  removeBtn:    { background: "none", border: "1px solid #CFD8DC", color: "#607D8B",
                  padding: "6px 16px", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  btn:          { width: "100%", padding: 14, background: "#1B3F6E", color: "#fff", border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 600, letterSpacing: ".02em", marginBottom: 0 },
  progressBar:  { height: 4, background: "#E3F2FD", borderRadius: 2, overflow: "hidden", marginTop: 8 },
  progressFill: { height: "100%", background: "#2E86AB", width: "60%",
                  animation: "progress 1.5s ease-in-out infinite" },
  resultCard:   { background: "#fff", borderRadius: 14, padding: 24,
                  boxShadow: "0 1px 4px rgba(0,0,0,.08)" },
  resultHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  successIcon:  { background: "#E8F5EE", color: "#1A7A4A", width: 32, height: 32,
                  borderRadius: "50%", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 16, fontWeight: 700, flexShrink: 0 },
  resultTitle:  { fontSize: 16, fontWeight: 600, color: "#1B3F6E" },
  statsGrid:    { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 },
  errorBox:     { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8,
                  padding: "12px 16px", color: "#B71C1C", fontSize: 13, marginBottom: 12 },
  warningBox:   { background: "#E8F5EE", border: "1px solid #A5D6A7", borderRadius: 8,
                  padding: "12px 16px", color: "#1A7A4A", fontSize: 13, marginBottom: 12 },
  warnBox:      { background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 8,
                  padding: "12px 16px", color: "#C85A00", fontSize: 13, marginBottom: 12 },
  helpCard:     { background: "#fff", borderRadius: 12, padding: 20, marginTop: 20,
                  boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  helpTitle:    { fontSize: 13, fontWeight: 600, color: "#455A64", marginBottom: 12 },
  helpSteps:    {},
};
